// Dictée vocale — API Web Speech du navigateur.
//
// Aucune dépendance, aucune clé, aucun envoi vers nos serveurs : la
// reconnaissance est faite par le navigateur. C'est ce qui rend la
// fonctionnalité acceptable ici — un produit qui promet l'auditabilité de ses
// données de recherche ne peut pas expédier discrètement l'audio de ses
// utilisateurs à un tiers pour transcrire un champ de formulaire.
//
// Le revers est le support inégal : Chrome et Edge l'implémentent (sous le
// préfixe `webkit`), Safari partiellement, Firefox pas du tout. On ne
// SIMULE donc rien — là où l'API n'existe pas, le bouton n'est pas rendu, et
// la saisie clavier reste ce qu'elle a toujours été.
import { useCallback, useEffect, useRef, useState } from 'react';

// L'API n'est pas encore dans les types standards de TypeScript : on déclare le
// strict nécessaire plutôt que d'installer un paquet de types entier.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Messages en clair : « not-allowed » ne veut rien dire pour qui dicte une
 *  note de terrain. On dit ce qui s'est passé ET quoi faire. */
function messageFor(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Micro refusé. Autorisez-le dans les réglages du navigateur, puis réessayez.';
    case 'audio-capture':
      return 'Aucun micro détecté.';
    case 'network':
      return 'La reconnaissance vocale n’a pas pu joindre le service du navigateur.';
    case 'no-speech':
      return 'Rien n’a été entendu.';
    case 'aborted':
      return '';
    default:
      return 'La dictée s’est interrompue.';
  }
}

export interface Dictation {
  /** `false` sur les navigateurs sans l'API : ne rien afficher du tout. */
  supported: boolean;
  listening: boolean;
  error: string | null;
  toggle: () => void;
}

/**
 * `onText` reçoit chaque bribe FINALE reconnue, au fil de la parole. À
 * l'appelant de décider quoi en faire — ici, toujours : l'ajouter à la suite de
 * ce qui est déjà saisi, jamais le remplacer. Dicter ne doit pas effacer.
 */
export function useDictation(onText: (text: string) => void): Dictation {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);

  // La fonction de rappel change à chaque rendu (elle capture la valeur du
  // champ) : on la garde dans une référence, sinon la reconnaissance en cours
  // continuerait d'écrire à partir d'une valeur périmée.
  const onTextRef = useRef(onText);
  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);

  const supported = recognitionCtor() !== null;

  // Un composant démonté alors que le micro écoute laisserait la capture
  // ouverte : le navigateur garderait l'indicateur d'enregistrement allumé.
  useEffect(
    () => () => {
      recRef.current?.abort();
      recRef.current = null;
    },
    [],
  );

  const toggle = useCallback(() => {
    if (recRef.current) {
      recRef.current.stop();
      return;
    }
    const Ctor = recognitionCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = 'fr-FR';
    // `continuous` : on dicte un paragraphe, pas un mot-clé — s'arrêter à la
    // première pause obligerait à recliquer entre chaque phrase.
    rec.continuous = true;
    // Seules les bribes FINALES sont écrites. Les résultats intermédiaires
    // changent à chaque syllabe ; les injecter ferait danser le texte sous
    // l'œil et rendrait toute correction manuelle impossible.
    rec.interimResults = false;

    rec.onresult = (e) => {
      let text = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const result = e.results[i];
        if (result && result.isFinal) text += result[0]?.transcript ?? '';
      }
      const trimmed = text.trim();
      if (trimmed) onTextRef.current(trimmed);
    };

    rec.onerror = (e) => {
      const msg = messageFor(e.error);
      if (msg) setError(msg);
    };

    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };

    setError(null);
    recRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch {
      // `start()` sur une instance déjà démarrée lève : on se remet d'aplomb
      // plutôt que de laisser le bouton bloqué en « écoute ».
      recRef.current = null;
      setListening(false);
    }
  }, []);

  return { supported, listening, error, toggle };
}

/** Ajoute la bribe dictée à la suite du texte existant, en gérant l'espace. */
export function appendDictated(current: string, addition: string): string {
  if (!current) return addition;
  return /\s$/.test(current) ? current + addition : `${current} ${addition}`;
}
